// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
import { ISchemaColumn, ITableHeader, ITableFilter, FilterTypeEnum } from '../../../../../../CommonScripts/Abstractions';
import { Feature } from 'GeoJSON';
declare const Cadcorp: any;
const isoDateRegEx = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z)?$/;

/**
 * @class
 * @classdesc A factory to get data for a data table.
 */
export class DataTableFactory {
    constructor() { }
    getHeader(schema: ISchemaColumn[], sortable: boolean): ITableHeader[] {
        let headers: ITableHeader[] = null;
        if (schema && schema.length > 0) {
            headers = [];
            
            for (let c = 0; c < schema.length; c++) {
                const schemaCol: ISchemaColumn = schema[c];
                const AllowedFilters = this.getAllowedFilters(schemaCol)
                const header: ITableHeader = {
                    sortable: sortable,
                    align: 'start',
                    text: schemaCol.name,
                    value: schemaCol.name,
                    type: schemaCol.type,
                    typeName: schemaCol.typeName,
                    sort: sortable ? this.getSortFunction(schemaCol): undefined,
                    closable: true,
                    filterable: AllowedFilters !== FilterTypeEnum.None,
                    allowedFilters: AllowedFilters
                }
                headers.push(header);
            }
            const actionHeader: ITableHeader = {
                sortable: false,
                align: 'start',
                text: '',
                value: '__actions',
                type: null,
                typeName: null,
                closable: false,
                filterable: false,
                allowedFilters: FilterTypeEnum.None
            }
            headers.push(actionHeader);
        }

        return headers;
    }

    getCSVRow(layer: any, rowData: any, header: ITableHeader[]): string[] {
        const replacer = (key, value) => value === null ? '' : value; // specify how you want to handle null values here

        const retVal = header.map((header) => {
            return JSON.stringify(this.getCellData(layer, rowData, header), replacer);
        });
        return retVal;
    }
    getCellData(layer: any, rowData: any, header: ITableHeader): any {
        let retVal = null;
        if (layer.getSource().overlay) {
            retVal = this.getSISCellData(rowData, header);
        } else
            retVal = this.getGeoJsonCellData(rowData, header);
        return retVal;
    }
    private getAllowedFilters(schemaCol: ISchemaColumn): FilterTypeEnum {
        let allowed = FilterTypeEnum.None;
        switch (schemaCol.type) {
            case 7: //date
                allowed = FilterTypeEnum.Equals | FilterTypeEnum.GreaterThan | FilterTypeEnum.GreaterThanEquals | FilterTypeEnum.LessThan | FilterTypeEnum.LessThanEquals;
                break;
            case 11: //boolean
                allowed = FilterTypeEnum.Equals;
                break;
            case 8: //string $ type columns
                allowed = FilterTypeEnum.Contains | FilterTypeEnum.Equals;
                break;
            // numeric types that are actually returned as numbers from geognosis
            case 2: //short int
            case 4: //single type
            case 5: //numeric # type column
            case 3: //integer type column & suffix
            case 6: //currency
            case 18://unsigned short
            case 16://byte
            case 17://unsigned byte
                allowed = FilterTypeEnum.Equals | FilterTypeEnum.GreaterThan | FilterTypeEnum.GreaterThanEquals | FilterTypeEnum.LessThan | FilterTypeEnum.LessThanEquals;
                break;
            // numeric types that are actually returned as strings from geognosis so filtering will be limited to equals
            case 19: //Unsigned long
            case 20: //Long Long int
            case 21: //Unsigned long long
            case 14://decimal
                allowed = FilterTypeEnum.Equals;
                break;
            default: //anything we don't know about is not allowed
                allowed = FilterTypeEnum.None;
                break;
        }
        return allowed;
    }
    private getGeoJsonCellData(rowData: any, header: ITableHeader): any {
        let retVal = null;
        switch (header.type) {
            case 8: // strings..but are they
                const value = rowData.properties[header.value];
                if (value) {
                    retVal = Cadcorp.Core.Utils.FormatValue(value);
                }
                break;
            case 7: //date
                // create a date object
                if (rowData.properties[header.value]) {
                    let tmpDate: any = new Date(rowData.properties[header.value]);
                    retVal = Cadcorp.Core.Utils.FormatDate(tmpDate);
                }
                break;
            default: //anything else
                retVal = rowData.properties[header.value];
                break;
        }
        return retVal;
    }

    private getSISCellData(rowData: any, header: ITableHeader): any {
        let retVal = null;
        switch (header.type) {
            case 8: //string $ type columns
            case 5: //numeric # type column
            case 3: // & type column
            case 11: //boolean
                //these are the original types, apart from boolean, so do nothing
                // The boolean type is a javascript primitive so it gets no special treatment
                retVal = rowData.properties[header.value];
                break;
            case 7: //date
                // create a date object
                if (rowData.properties[header.value] && rowData.properties[header.value][header.typeName]) {
                    let tmpDate: Date = new Date(rowData.properties[header.value][header.typeName]);
                    retVal = Cadcorp.Core.Utils.FormatDate(tmpDate);
                }
                break;
            //case 19:// Unsigned Long Int
            //case 20:// Long Long Int
            //case 21:// Unsigned Long Long Int
            // because of their size these numbers are sent as strings, so we won't change that here
            default: //anything else
                if (rowData.properties[header.value] && rowData.properties[header.value][header.typeName])
                    retVal = rowData.properties[header.value][header.typeName];
                else
                    retVal = rowData.properties[header.value];

                break;
        }
        return retVal;
    }

    getSchemaColumns(layer: any): Promise<ISchemaColumn[]> {
        let retVal: Promise<ISchemaColumn[]> = null;
        if (layer.getSource().overlay) {
            retVal = layer.getSource().overlay.getSchemaColumns();
        }
        else {
            retVal = new Promise((resolve, reject) => {
                let features = layer.getSource()?.getFeatures();
                const feature = features && features.length > 0 ? features[0] : null;
                if (feature) {
                    const props = feature.getProperties();
                    const schema: ISchemaColumn[] = [];
                    let count = 0;
                    for (let prop in props) {
                        if (props.hasOwnProperty(prop)) {
                            let type = this.getColumnType(props[prop]);
                            if (type) {
                                schema.push({
                                    name: prop,
                                    columnIndex: count,
                                    expression: prop,
                                    isHidden: false,
                                    isMapTip: false,
                                    typeName: type.typeName,
                                    type: type.type
                                });
                                count++;
                            }
                        }
                    }
                    resolve(schema);
                }
                else {
                    reject('no features in layer');
                }

            });
        }
        return retVal;
    }
    private getColumnType(property: any): { type: number, typeName: string } {
        let retVal: { type: number, typeName: string } = null;
        if (property) {
            switch (typeof property) {
                case 'number':
                    if (Number.isInteger) {
                        if (Number.isInteger(property)) {
                            retVal = { type: 20, typeName: 'i8' };
                        } else {
                            retVal = { type: 14, typeName: 'decimal' };
                        }
                    } else {
                        retVal = { type: 14, typeName: 'decimal' };
                    }
                    break;
                case 'boolean':
                    retVal = { type: 11, typeName: 'boolean' };
                    break;
                case 'string':
                    if (!isoDateRegEx.test(property)) {
                        retVal = { type: 8, typeName: 'string' };
                    } else {
                        retVal = { type: 7, typeName: 'date' };
                    }
                    break;
                default:
                    break;
            }
        }
        else
            retVal = { type: 8, typeName: 'string' };
        return retVal;
    }

    private getSortFunction(schemaCol: ISchemaColumn): (a: any, b: any) => number {
        const dateFunc = (a: any, b: any) => {
            let retVal = 0;
            if (a !== null && b !== null) {
                const aDate = new Date(a[schemaCol.typeName]);
                const bDate = new Date(b[schemaCol.typeName]);
                retVal = aDate.getTime() - bDate.getTime();
            }
            else {//1 or both values are null
                if (a === b)
                    retVal = 0; //both null
                else if (a === null)
                    retVal = 1;
                else
                    retVal = -1;
            }
            return retVal;
        };
        const simpleFunc = (a: any, b: any) => {
            let retVal = 0;
            if (a !== null && b !== null)// no null values
                retVal = a - b;
            else {
                if (a == b)
                    retVal = 0;
                else if (a === null)
                    retVal = 1;
                else
                    retVal = -1;
            }

            return retVal;
        };
        const stringFunc = (a: any, b: any) => {
            let retVal = 0;
            if (a !== null && b !== null)// no null values
                retVal = a.localeCompare(b);
            else {
                if (a == b)
                    retVal = 0;
                else if (a === null)
                    retVal = 1;
                else
                    retVal = -1;
            }

            return retVal;
        };
        const objectFunc = (a: any, b: any) => {
            return stringFunc(a[schemaCol.typeName], b[schemaCol.typeName]);
        };
        let retVal = null;
        switch (schemaCol.type) {
            case 8: //string $ type columns
                retVal = stringFunc;
                break;
            case 5: //numeric # type column
            case 3: // & type column
            case 11: //boolean
                //these are the original types, apart from boolean, so do nothing
                // The boolean type is a javascript primitive so it gets no special treatment
                retVal = simpleFunc;
                break;
            case 7: //date
                retVal = dateFunc;
                break;
            default: //anything else
                retVal = objectFunc;
                break;
        }
        return retVal;
    }
    filterData(features: Feature[], filter: ITableFilter): Feature[] {
        let retVal: Feature[] = null;
        const newFilter = this.getCorrectFilter(filter)
        const filterFunc = this.getFilterFunction(newFilter);
        if (filterFunc) {
            retVal = features.filter((element) => {
                return filterFunc(element);
            });
        }
        else
            retVal = features;
        return retVal;
    }
    private getCorrectFilter(filter: ITableFilter): ITableFilter {
        let newFilter: ITableFilter = { ...filter };
        switch (filter.searchColumn.type) {

            // numeric types that are actually returned as numbers from geognosis
            // integer types
            case 2: //short int
            case 4: //single type
            case 3: //integer type column & suffix
            case 18://unsigned short
            case 16://byte
            case 17://unsigned byte
                newFilter.value = parseInt(filter.value, 10);
                break;
            //decimal types
            case 5: //numeric # type column
            case 6: //currency
                newFilter.value = parseFloat(filter.value);
                break;
            // types that are actually returned as strings
            case 7: //date
            case 8: //string $ column type
            case 19: //Unsigned long
            case 20: //Long Long int
            case 21: //Unsigned long long
            case 14://decimal
            default: //don't try to convert it
                newFilter.value = filter.value;
                break;
        }
        return newFilter;

    }
    private getFilterFunction(filter: ITableFilter): (feature: Feature) => boolean {
        let func = null;
        let testDate = null;
        if (filter.searchColumn.type === 7) {
            testDate = Date.parse(filter.value)
        }
        switch (filter.filterType) {
            case FilterTypeEnum.Contains:
                func = (feature: Feature) => {
                    let retVal = false;
                    if (filter.searchColumn.type === 8) {// only works with string type data
                        const value: string = this.getFeaturePropValue(feature, filter.searchColumn);
                        if (value)
                            retVal = value.includes(filter.value);
                    }
                    return retVal;
                }
                break;
            case FilterTypeEnum.Equals:
                func = (feature: Feature) => {
                    let retVal = false;
                    const value = this.getFeaturePropValue(feature, filter.searchColumn);
                    if (filter.searchColumn.type !== 7) {// anything other than dates
                        retVal = value === filter.value;
                    }
                    else { //dates
                        if (value) {
                            const featureDate = Date.parse(value);
                            retVal = featureDate === testDate;
                        }
                    }
                    return retVal;
                }
                break;
            case FilterTypeEnum.GreaterThan:
                func = (feature: Feature) => {
                    let retVal = false;
                    const value = this.getFeaturePropValue(feature, filter.searchColumn);
                    if (filter.searchColumn.type !== 7) {// anything other than dates
                        retVal = value > filter.value;
                    }
                    else { //dates
                        if (value) {
                            const featureDate = Date.parse(value);
                            retVal = featureDate > testDate;
                        }
                    }
                    return retVal;
                }
                break;
            case FilterTypeEnum.GreaterThanEquals:
                func = (feature: Feature) => {
                    let retVal = false;
                    const value = this.getFeaturePropValue(feature, filter.searchColumn);
                    if (filter.searchColumn.type !== 7) {// anything other than dates
                        retVal = value >= filter.value;
                    }
                    else { //dates
                        if (value) {
                            const featureDate = Date.parse(value);
                            retVal = featureDate >= testDate;
                        }
                    }
                    return retVal;
                }
                break;
            case FilterTypeEnum.LessThan:
                func = (feature: Feature) => {
                    let retVal = false;
                    const value = this.getFeaturePropValue(feature, filter.searchColumn);
                    if (filter.searchColumn.type !== 7) {// anything other than dates
                        retVal = value < filter.value;
                    }
                    else { //dates
                        if (value) {
                            const featureDate = Date.parse(value);
                            retVal = featureDate < testDate;
                        }
                    }
                    return retVal;
                }
                break;
            case FilterTypeEnum.LessThanEquals:
                func = (feature: Feature) => {
                    let retVal = false;
                    const value = this.getFeaturePropValue(feature, filter.searchColumn);
                    if (filter.searchColumn.type !== 7) {// anything other than dates
                        retVal = value <= filter.value;
                    }
                    else { //dates
                        if (retVal) {
                            const featureDate = Date.parse(value);
                            retVal = featureDate <= testDate;
                        }
                    }
                    return retVal;
                }
                break;
            default:
                func = null;
        }
        return func;
            
    }
    private getFeaturePropValue(feature: Feature, header: ITableHeader) {
        let retVal = null;
        switch (header.type) {
            case 8: //string $ type columns
            case 5: //numeric # type column
            case 3: // & type column
            case 11: //boolean
                //these are the original types, apart from boolean, so do nothing
                // The boolean type is a javascript primitive so it gets no special treatment
                retVal = feature.properties[header.value];
                break;
            //case 19:// Unsigned Long Int
            //case 20:// Long Long Int
            //case 21:// Unsigned Long Long Int
            // because of their size these numbers are sent as strings, so we won't change that here
            default: //anything else
                if (feature.properties[header.value] && feature.properties[header.value][header.typeName])
                    retVal = feature.properties[header.value][header.typeName];
                else
                    retVal = feature.properties[header.value];

                break;
        }
        return retVal;
    }
}
/*switch (schemaColumn.type) {
    case 7: //date
        break;
    case 11: //boolean
        break;
    case 8: //string
    case 5: //numeric
        break;
    case 6: //currency
        break;
    case 14:// decimal numbers
    case 4:
        break;
    case 3:
        break;
    case 2: // integer values
    case 20:
        break;
    case 18://unsigned integers
    case 19:
    case 21:
        break;
    default:
        break;
}*/


